import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AppNavigation from '../navigation/AppNavigation';
import { ToastProvider } from '@/hooks/useToast';
import { AssistantActionProvider } from '@/contexts/AssistantActionContext';
import { useAuth } from '@/context/AuthContext';
import QuotaNotificationBanner from '../common/QuotaNotificationBanner';
import QuotaNotificationListener from '../common/QuotaNotificationListener';
import Assistant from '../assistant/Assistant';
import { EditorProvider } from '@/context/EditorContext';
import { apiConfig } from '@/config/api';
import { ProfileProvider } from '@/contexts/ProfileContext';

const AppLayout: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!loading && !user) {
      setIsRedirecting(true);
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      // Use configured WWW URL
      window.location.href = `${apiConfig.wwwUrl}/signin/?next=${next}`;
    }
  }, [loading, user]);

  // Check if user needs email verification (allow verify-email-required page itself)
  const needsVerification = user && !user.emailVerified && location.pathname !== '/verify-email-required';

  if (loading || isRedirecting) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Double check to prevent flash of content
  if (!user) return null;

  // Redirect to email verification page if not verified
  if (needsVerification) {
    window.location.href = '/verify-email-required';
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <ProfileProvider>
      <EditorProvider>
        <QuotaNotificationListener />
        <QuotaNotificationBanner />
        <AppNavigation />
        <Outlet />
        <Assistant />
      </EditorProvider>
    </ProfileProvider>
  );
};

export default AppLayout;
